import { useEffect, useState, useCallback, useRef } from 'react';
import dayjs from 'dayjs';
import { useAuth } from '../context/useAuth';
import { jwtDecode } from 'jwt-decode';
import { refreshAccessToken } from '../api';

/**
 * useSessionInactivity Hook
 *
 * Tracks user activity and manages the session expiry warning toast.
 * - Warning appears 30s before logout (changed from 60s)
 * - Any mouse/click/key activity while the warning is showing auto-extends
 *   the session without requiring a button click
 */
export function useSessionInactivity(warningThresholdSeconds = 30) {
    const { token, lastActivity, resetInactivity, logout } = useAuth();
    const [isWarningOpen, setIsWarningOpen] = useState(false);
    const [secondsLeft, setSecondsLeft] = useState(warningThresholdSeconds);
    const [isExtending, setIsExtending] = useState(false);

    // Refs to avoid stale closures in the interval
    const tokenRef = useRef(token);
    const isWarningOpenRef = useRef(isWarningOpen);
    const isExtendingRef = useRef(isExtending);
    const warningThresholdRef = useRef(warningThresholdSeconds);
    const lastActivityRef = useRef(lastActivity);
    const lastExtendedAtRef = useRef<number>(0);

    // Sync refs with state
    useEffect(() => { tokenRef.current = token; }, [token]);
    useEffect(() => { isWarningOpenRef.current = isWarningOpen; }, [isWarningOpen]);
    useEffect(() => { isExtendingRef.current = isExtending; }, [isExtending]);
    useEffect(() => { warningThresholdRef.current = warningThresholdSeconds; }, [warningThresholdSeconds]);
    useEffect(() => { lastActivityRef.current = lastActivity; }, [lastActivity]);

    const extendSession = useCallback(async () => {
        if (isExtendingRef.current || !tokenRef.current) return;

        setIsExtending(true);

        try {
            const newToken = await refreshAccessToken();
            if (newToken) {
                lastExtendedAtRef.current = dayjs().valueOf();
                resetInactivity();
                setIsWarningOpen(false);
                setSecondsLeft(warningThresholdRef.current);
            } else {
                console.warn('[SessionInactivity] Refresh returned no token.');
                logout();
            }
        } catch (err) {
            console.error('[SessionInactivity] Refresh error:', err);
            logout();
        } finally {
            setIsExtending(false);
        }
    }, [logout, resetInactivity]);

    // Reset warning state when token changes (e.g. new login)
    useEffect(() => {
        if (token) {
            setIsWarningOpen(false);
            setSecondsLeft(warningThresholdRef.current);
        }
    }, [token]);

    const closeWarning = useCallback(() => {
        setIsWarningOpen(false);
    }, []);

    // ─── Unified Monitoring Interval (1s tick) ────────────────────────────
    useEffect(() => {
        if (!token) return;

        const interval = setInterval(() => {
            const currentToken = tokenRef.current;
            if (!currentToken || isExtendingRef.current) return;

            try {
                const decoded: any = jwtDecode(currentToken);
                const iatMs = (decoded.iat || (decoded.exp - 900)) * 1000;
                const expiryMs = decoded.exp * 1000;
                const now = dayjs().valueOf();

                const totalTokenLifeSec = Math.floor((expiryMs - iatMs) / 1000);
                const secondsSinceIssue = Math.max(0, Math.floor((now - iatMs) / 1000));
                const tokenSecLeft = totalTokenLifeSec - secondsSinceIssue;

                const inactiveMs = now - lastActivityRef.current;
                const inactiveSec = Math.floor(inactiveMs / 1000);
                const sessionSecLeft = totalTokenLifeSec - inactiveSec;

                // A. Force logout if token is dead OR inactivity limit reached
                if (secondsSinceIssue > 2) {
                    if (tokenSecLeft <= -2) {
                        console.warn('[SessionInactivity] Token expired (hard). Logging out.');
                        clearInterval(interval);
                        logout();
                        return;
                    }
                    if (sessionSecLeft <= -2) {
                        console.warn('[SessionInactivity] Inactivity limit reached (hard). Logging out.');
                        clearInterval(interval);
                        logout();
                        return;
                    }
                }

                // B. If token is getting old but user IS active -> silent refresh
                const tokenLifeUsed = totalTokenLifeSec > 0 ? (secondsSinceIssue / totalTokenLifeSec) : 0;
                if (tokenLifeUsed > 0.75 && inactiveSec < 30 && !isWarningOpenRef.current) {
                    extendSession();
                    return;
                }

                // C. Show warning toast if inactivity is approaching the limit
                if (sessionSecLeft <= warningThresholdRef.current) {
                    if (!isWarningOpenRef.current) {
                        setIsWarningOpen(true);
                    }
                    setSecondsLeft(Math.max(0, sessionSecLeft));
                } else if (isWarningOpenRef.current) {
                    setIsWarningOpen(false);
                }
            } catch (err) {
                console.error('[SessionInactivity] Timer error:', err);
            }
        }, 1000);

        return () => { clearInterval(interval); };
    }, [token, logout, extendSession]);

    // ─── Activity Listener ────────────────────────────────────────────────
    // Activity while warning is showing auto-extends the session (no button click needed).
    useEffect(() => {
        if (!token) return;

        let lastEvent = 0;
        const handleInteraction = () => {
            resetInactivity();

            const currentToken = tokenRef.current;
            if (!currentToken || isExtendingRef.current) return;

            try {
                const decoded: any = jwtDecode(currentToken);
                const iatMs = (decoded.iat || (decoded.exp - 300)) * 1000;
                const expiryMs = decoded.exp * 1000;
                const tokenLifeUsed = (dayjs().valueOf() - iatMs) / (expiryMs - iatMs);

                // Auto-extend if: warning is showing OR token is nearly expired
                if ((isWarningOpenRef.current || tokenLifeUsed > 0.75) && !isExtendingRef.current) {
                    extendSession();
                }
            } catch (e) { /* ignore decode errors */ }
        };

        const events = ['mousedown', 'keydown', 'scroll', 'click', 'mousemove'];
        const throttled = () => {
            // Only count activity when this tab/window actually has focus.
            // mousemove fires even when the browser is in the background,
            // so without this guard hovering over an unfocused window would extend the session.
            if (!document.hasFocus()) return;

            const now = dayjs().valueOf();
            if (now - lastEvent > 2000) {
                handleInteraction();
                lastEvent = now;
            }
        };

        events.forEach(e => window.addEventListener(e, throttled, { passive: true }));
        return () => events.forEach(e => window.removeEventListener(e, throttled));
    }, [token, resetInactivity, extendSession]);

    return {
        isWarningOpen,
        secondsLeft,
        isExtending,
        extendSession,
        closeWarning
    };
}
